import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { FaCheckCircle, FaUsers, FaBus, FaShieldAlt, FaArrowRight } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import Cta from '../../components/NewCallToAction';

const BRAND_BLUE = "#3b82f6";
const DARK_SLATE = "#0f172a";

const ServiceDetailPage = ({ title, subtitle, heroBg, description, highlights, coreCompetencies, faqs }) => {
  return (
    <main style={{ background: '#f8fafc', minHeight: '100vh', overflowX: 'hidden' }}>
      <Helmet>
        <title>{title} | Abraj Al Dhafna Qatar</title>
        <meta name="description" content={description.substring(0, 155)} />
        <link rel="canonical" href={`https://abrajaldhafna.com/services/${title.toLowerCase().replace(/ /g, '-')}`} />
      </Helmet>

      {/* Breadcrumb & Back */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '15px 0' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px', display: 'flex', gap: '10px', alignItems: 'center', fontSize: '14px' }}>
          <Link to="/" style={{ color: '#64748b', textDecoration: 'none' }}>Home</Link>
          <span style={{ color: '#cbd5e1' }}>/</span>
          <Link to="/services" style={{ color: '#64748b', textDecoration: 'none' }}>Services</Link>
          <span style={{ color: '#cbd5e1' }}>/</span>
          <span style={{ color: DARK_SLATE, fontWeight: 600 }}>{title}</span>
        </div>
      </div>

      {/* Header / Hero Section */}
      <section style={{ background: heroBg, padding: '100px 20px 80px', color: '#fff', position: 'relative', overflow: 'hidden' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 2 }}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span style={{ background: 'rgba(255,255,255,0.2)', padding: '6px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 700, letterSpacing: '1px', marginBottom: '20px', display: 'inline-block' }}>CORE CAPABILITY</span>
            <h1 style={{ fontSize: 'clamp(2.5rem, 5vw, 3.5rem)', fontWeight: 900, marginBottom: '20px', lineHeight: 1.1 }}>{title}</h1>
            <p style={{ fontSize: 'clamp(1.1rem, 2vw, 1.3rem)', opacity: 0.9, maxWidth: '700px', lineHeight: 1.6 }}>{subtitle}</p>
          </motion.div>
        </div>
        <div style={{ position: 'absolute', bottom: '-50px', right: '-50px', width: '300px', height: '300px', background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)', borderRadius: '50%' }} />
      </section>

      {/* Main Narrative Section */}
      <section style={{ padding: '80px 20px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '60px' }}>
          <div>
            <h2 style={{ fontSize: '2rem', fontWeight: 800, color: DARK_SLATE, marginBottom: '25px' }}>Strategic Manpower Deployment</h2>
            <div style={{ fontSize: '16px', lineHeight: 1.8, color: '#475569', marginBottom: '30px' }}>
              <p style={{ marginBottom: '20px' }}>{description}</p>
              <p>We operate as an authorized manpower leasing and supply partner, adhering 100% to Qatar Ministry of Labour standards. By maintaining our own fleet of air-conditioned 15 and 30-seater buses, we eliminate third-party transit delays, ensuring our personnel reach your project gate completely on time and ready to produce.</p>
            </div>

            <h3 style={{ fontSize: '1.4rem', fontWeight: 700, color: DARK_SLATE, marginBottom: '20px' }}>Key Service Highlights</h3>
            <ul style={{ listStyle: 'none', padding: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
              {highlights.map((highlight, i) => (
                <li key={i} style={{ display: 'flex', gap: '12px', alignItems: 'center', fontSize: '15px', color: '#475569' }}>
                  <FaCheckCircle style={{ color: "#ea580c", flexShrink: 0 }} />
                  <span>{highlight}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Side Stats / Summary */}
          <div style={{ background: '#fff', borderRadius: '24px', padding: '40px', boxShadow: '0 10px 30px rgba(0,0,0,0.03)', border: '1px solid #e2e8f0', height: 'fit-content' }}>
            <h3 style={{ fontSize: '1.3rem', fontWeight: 800, color: DARK_SLATE, marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #f1f5f9' }}>Project Capabilities</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div>
                <span style={{ fontSize: '12px', textTransform: 'uppercase', fontWeight: 800, color: '#94a3b8', display: 'block', marginBottom: '5px' }}>Worker Fleet Range</span>
                <strong style={{ fontSize: '18px', color: DARK_SLATE }}>400+ Skilled Tradesmen Pool</strong>
              </div>
              <div>
                <span style={{ fontSize: '12px', textTransform: 'uppercase', fontWeight: 800, color: '#94a3b8', display: 'block', marginBottom: '5px' }}>Compliance & WPS</span>
                <strong style={{ fontSize: '18px', color: DARK_SLATE }}>100% Legal Hygiene (WPS Enabled)</strong>
              </div>
              <div>
                <span style={{ fontSize: '12px', textTransform: 'uppercase', fontWeight: 800, color: '#94a3b8', display: 'block', marginBottom: '5px' }}>Transportation Support</span>
                <strong style={{ fontSize: '18px', color: DARK_SLATE }}>Direct In-House Transit AC Fleet</strong>
              </div>
            </div>
            <Link to="/contact" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', marginTop: '40px', background: "#ea580c", color: '#fff', textDecoration: 'none', padding: '16px', borderRadius: '12px', fontWeight: 700, transition: 'transform 0.2s' }}>
              Request A Quote <FaArrowRight />
            </Link>
          </div>
        </div>
      </section>

      {/* In-Depth Competencies */}
      <section style={{ background: '#fff', padding: '80px 20px', borderTop: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{ fontSize: '2rem', fontWeight: 900, color: DARK_SLATE, textAlign: 'center', marginBottom: '50px' }}>Detailed Competency Portfolio Portfolio</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '30px' }}>
            {coreCompetencies.map((comp, i) => (
              <div key={i} style={{ background: '#f8fafc', padding: '30px', borderRadius: '20px', border: '1px solid #f1f5f9' }}>
                <div style={{ width: '50px', height: '50px', background: `#ea580c15`, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: "#ea580c", fontSize: '1.5rem', marginBottom: '20px' }}>{comp.icon}</div>
                <h4 style={{ fontSize: '1.2rem', fontWeight: 800, color: DARK_SLATE, marginBottom: '10px' }}>{comp.title}</h4>
                <p style={{ fontSize: '14px', color: '#64748b', lineHeight: 1.6 }}>{comp.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Cta />
    </main>
  );
};

const ManpowerSupply = () => {
  const pageData = {
    title: "Skilled Manpower Supply Qatar",
    subtitle: "Certified construction helpers, masons, welders, electricians, and rapid logistics mobilization.",
    heroBg: "linear-gradient(135deg, #c2410c 0%, #f97316 100%)",
    description: "Abraj Al Dhafna is a certified supplier of skilled, semi-skilled, and general manpower for Qatar's infrastructure projects. We maintain an extensive roster of tested tradesmen, ensuring that every mason, plumber, shuttering carpenter, and structural fixer possess valid certifications and site experience. With immediate mobilization capabilities, we support massive commercial projects, residential compounds, and industrial sites during peak production timelines.",
    highlights: [
      "Certified Masons & Shuttering Carpenters",
      "Tested Electricians & Mechanical Helpers",
      "Structural and Pipe Welders (Certified)",
      "General Laborers & Rapid Construction Crews",
      "Supervisory and Foreman Deployment",
      "100% WPS Compliant Legal Infrastructure"
    ],
    coreCompetencies: [
      {
        icon: <FaUsers />,
        title: "Pre-Vetted Skilled Trades",
        description: "Every worker passes thorough localized trade evaluation before deployment. This filters out operational drag and secures direct productivity gains."
      },
      {
        icon: <FaBus />,
        title: "Rapid Transport Logistics",
        description: "We command our own fleet of transit vehicles, meaning shifts are never late, ramp-ups happen immediately, and logistics costs remain flat."
      },
      {
        icon: <FaShieldAlt />,
        title: "Legal & Safety Hygiene",
        description: "We handle 100% of medical clearances, valid QID maintenance, safety PPE allocation, and monthly Wage Protection System verification."
      }
    ]
  };

  return <ServiceDetailPage {...pageData} />;
};

export default ManpowerSupply;
